import { IkeCloneable, IkeCollectorType, IkeFieldType, IkeToken, SubFormType } from '@ikegps/core-api';
import {
	FormModelField,
	FormModelFieldNestedList,
	FormModelFieldSelectList,
	FormModelFieldSubForm,
} from '@ikegps/core-shared';
import { action, makeObservable } from 'mobx';
import { ImageButton } from '../../../components/button/image.button';
import { Icon } from '../../../components/icon';
import { MaterialIcon } from '../../../components/icon/icons';
import { Input } from '../../../components/input/input';
import { CheckBox, type CheckBoxState } from '../../../components/input/input.checkbox';
import { InputState } from '../../../components/input/input.state';
import { IkeInputStateGroup } from '../../../components/input/input.state.group';
import { MenuComponent } from '../../../components/menu/menu';
import { type MenuItem, MenuItemComponent } from '../../../components/menu/menu.item';
import { ModalService } from '../../../components/modal/modal.service';
import { NotificationService } from '../../../components/notification.toast/notification.service';
import { addStyles } from '../../../components/theme';
import { T, Translate } from '../../../i18n';
import type { MithrilVNode } from '../../../mithril';
import { m } from '../../../mithril';
import { Services } from '../../../services';
import { FormDesigner } from '../form.designer';
import { FIELD_TYPE_DISPLAY, FormDesignerPalette } from '../form.palette';
import { FormFieldTreeView } from './form.field.tree.view';
import { CSS, Style } from './form.info.panel.style';

addStyles(Style);

export class FormFieldEditor<FT extends FormModelField = FormModelField> {
	field: FT;
	designer: FormDesigner;

	state: IkeInputStateGroup = new IkeInputStateGroup();
	menu: MenuComponent;

	constructor(designer: FormDesigner, field: FT) {
		makeObservable(this);
		this.designer = designer;
		this.field = field;
		this.state.isLabelShown = false;
		this.menu = new MenuComponent({
			items: () => this.viewMenuItems(),
			className: CSS.menu,
		});
	}

	get isLabelable() {
		switch (this.field.type) {
			case IkeFieldType.TEXT:
			case IkeFieldType.NUMBER:
			case IkeFieldType.SELECT_LIST:
			case IkeFieldType.NESTED_LIST:
				return true;
			default:
				return false;
		}
	}

	get isCloneable() {
		switch (this.field.type) {
			case IkeFieldType.DATE:
			case IkeFieldType.FORM:
			case IkeFieldType.NESTED_LIST:
			case IkeFieldType.NUMBER:
			case IkeFieldType.ORIENTATION:
			case IkeFieldType.SELECT_LIST:
			case IkeFieldType.TEXT:
			case IkeFieldType.YES_NO:
				return true;
			default:
				return false;
		}
	}

	get isStatusable() {
		const validFieldTypes: Partial<IkeFieldType>[] = [IkeFieldType.NESTED_LIST, IkeFieldType.SELECT_LIST];

		return this.field.isRoot && validFieldTypes.includes(this.field.type);
	}

	get isMapColorable() {
		const validFieldTypes: Partial<IkeFieldType>[] = [IkeFieldType.NESTED_LIST, IkeFieldType.SELECT_LIST];

		return this.field.isRoot && validFieldTypes.includes(this.field.type);
	}

	get isAddressable() {
		const validFieldTypes: Partial<IkeFieldType>[] = [IkeFieldType.TEXT];

		return validFieldTypes.includes(this.field.type);
	}

	get isRequirable() {
		const invalidFieldTypes: Partial<IkeFieldType>[] = [
			IkeFieldType.FORM,
			IkeFieldType.VERTICAL_SEPARATION,
			IkeFieldType.YES_NO,
			IkeFieldType.FILE_TOOL,
		];

		return this.field.isRoot && !invalidFieldTypes.includes(this.field.type);
	}

	get depth() {
		let depth = 0;
		let parent: FormModelField = this.field;
		while (parent != null) {
			parent = parent.parent;
			depth += 1;
		}
		return depth;
	}

	@action.bound deleteField() {
		const parent = this.field.parent;
		this.field.Mutator.delete();
		this.state.destroy();
		this.designer.setSelected(parent);
		Services.Analytics.trackEvent(Services.Analytics.EventCategory.Mutate, 'form:field:delete');
	}

	@action.bound addField() {
		const field = this.field;
		if (!FormModelFieldSubForm.isSubForm(field)) {
			return;
		}

		const newField = field.createField(IkeFieldType.TEXT);
		newField.Mutator.adminOnly(field.adminOnly);
		newField.Mutator.collector(field.collector);
		newField.Mutator.required(field.required);
		newField.Mutator.annotationLabelFieldId(field.annotationLabelFieldId);
		newField.Mutator.clearanceThresholdInMeters(field.clearanceThresholdInMeters);
		this.designer.setSelected(newField);
		Services.Analytics.trackEvent(Services.Analytics.EventCategory.Mutate, 'form:tree:add', IkeFieldType.TEXT);
	}

	@action.bound private changeFieldType(type: IkeFieldType) {
		const originalType = this.field.type;
		if (type === originalType) {
			return;
		}

		if (
			FormModelFieldSelectList.isSelectList(this.field) &&
			this.field.options != null &&
			this.field.options.length > 1
		) {
			NotificationService.warn({ key: T.FORM_FIELD_CANNOT_CHANGE });
			return;
		}

		if (
			FormModelFieldNestedList.isNestedList(this.field) &&
			this.field.options != null &&
			this.field.options.values.length > 0
		) {
			NotificationService.warn({ key: T.FORM_FIELD_CANNOT_CHANGE });
			return;
		}

		if (FormModelFieldSubForm.isSubForm(this.field) && this.field.fields != null && this.field.fields.length > 0) {
			NotificationService.warn({ key: T.FORM_FIELD_CANNOT_CHANGE });
			return;
		}

		const parent = this.field.parent;
		const index = parent.getFieldIndex(this.field.id);

		const newField = parent.Mutator.create(type, this.field.id, index);
		newField.Mutator.displayName(this.field.displayName);
		newField.Mutator.collector(this.field.collector);
		newField.Mutator.adminOnly(this.field.adminOnly);
		newField.Mutator.annotationLabelFieldId(this.field.annotationLabelFieldId);
		newField.Mutator.clearanceThresholdInMeters(this.field.clearanceThresholdInMeters);

		if (newField.isCloneable) {
			newField.Mutator.cloneable(this.field.cloneable);
		}

		this.state.destroy();
		this.designer.clearCachedEditor(this.field.fieldId);
		this.designer.setSelected(newField);

		Services.Analytics.trackEvent(
			Services.Analytics.EventCategory.Mutate,
			'form:field:change-type',
			`${originalType} -> ${type}`
		);
	}

	/** View the field as a tree */
	viewTree() {
		return FormFieldTreeView.view(this);
	}

	/** View the field as a info panel */
	viewInfo() {
		if (this.designer.isEditing) {
			return this._viewInfoRW();
		}
		return this._viewInfoRO();
	}

	// Additional information view for children;
	viewInfoRW(): MithrilVNode {
		return null;
	}
	viewInfoRO(): MithrilVNode {
		return null;
	}

	private _viewInfoRO() {
		return m('div', { className: CSS.container }, [this.viewTitle(), this.viewOptions(), this.viewInfoRO()]);
	}

	private _viewInfoRW() {
		return m('div', { className: CSS.container }, [
			this.viewFieldMove(),
			this.viewTitle(),
			this.viewOptions(),
			this.viewInfoRW(),
		]);
	}

	protected viewTitle(): MithrilVNode {
		return m('div', { className: `${CSS.section.container}` }, [
			Input.view(this.getTitleState()),
			this.viewFieldTypeButton(),
			this.viewFieldDeleteButton(),
		]);
	}

	private viewFieldTypeButton(): MithrilVNode {
		return m('div', { className: CSS.container }, [
			m(
				'button',
				{
					className: `mdl-button ${CSS.type}`,
					title: Translate(T.FORM_FIELD_TYPE),
					disabled: this.field.isLocked,
					onclick: this.menu.show,
				},
				[
					FormDesignerPalette.viewIcon(this.field.type),
					FormDesignerPalette.translate(this.field.type),
					Icon.view(MaterialIcon.KEY_DOWN),
				]
			),
			this.menu.view(),
		]);
	}

	private viewFieldDeleteButton(): MithrilVNode {
		if (!this.designer.isEditing || this.field.isLocked) {
			return null;
		}

		return ImageButton.view(MaterialIcon.DELETE, {
			title: T.FORM_FIELD_DELETE,
			onclick: () => this.confirmDeleteField(),
		});
	}

	private viewOptions(): MithrilVNode {
		const canSetAdminOnly = Services.Token.has(IkeToken.FEATURE_GROUP_IKE_ANALYZE);

		const canSetStatusProperty =
			(Services.Token.has(IkeToken.FEATURE_GROUP_IKE_ANALYZE) || Services.Token.has(IkeToken.FEATURE_SORT_STATUS)) &&
			this.isStatusable;
		const canSetMapColorProperty = Services.Token.has(IkeToken.FEATURE_COLOR_FLAG) && this.isMapColorable;
		const canSetAddressProperty = Services.Token.has(IkeToken.FEATURE_GROUP_IKE_ANALYZE) && this.isAddressable;

		if (
			!this.isLabelable &&
			!this.isCloneable &&
			!canSetAdminOnly &&
			!canSetStatusProperty &&
			!canSetMapColorProperty &&
			!canSetAddressProperty
		) {
			return null;
		}

		const canSetRequiredProperty = this.isRequirable && Services.Token.has(IkeToken.FEATURE_GROUP_IKE_ANALYZE);

		const isRoot = this.field.isRoot;
		const canSecondaryLabel =
			(Services.Token.has(IkeToken.FEATURE_SECONDARY_LABEL) ||
				Services.Token.has(IkeToken.FEATURE_GROUP_IKE_ANALYZE)) &&
			this.isLabelable &&
			isRoot;
		return m('div', { className: `${CSS.section.container} ${CSS.section.final}` }, [
			this.isLabelable
				? this.viewOption(
						T.FORM_OPTION_LABEL,
						CheckBox.view(this.getLabelableState()),
						Translate(T.FORM_OPTION_LABEL_TITLE, { isRoot })
					)
				: null,
			canSecondaryLabel
				? this.viewOption(
						T.FORM_OPTION_SECONDARY_LABEL,
						CheckBox.view(this.getSecondarylabelableState()),
						Translate(T.FORM_OPTION_SECONDARY_LABEL)
					)
				: null,
			this.isCloneable
				? this.viewOption(
						T.FORM_OPTION_CLONEABLE,
						CheckBox.view(this.getCloneableState()),
						Translate(T.FORM_OPTION_CLONEABLE_TITLE)
					)
				: null,
			this.viewOption(T.FORM_OPTION_HIDE, CheckBox.view(this.getHiddenState()), Translate(T.FORM_OPTION_HIDE_TITLE)),
			canSetAdminOnly
				? this.viewOption(
						T.FORM_OPTION_ADMIN_ONLY,
						CheckBox.view(this.getAdminOnlyState()),
						Translate(T.FORM_OPTION_ADMIN_ONLY_TITLE)
					)
				: null,
			canSetStatusProperty
				? this.viewOption(
						T.FORM_OPTION_STATUS,
						CheckBox.view(this.getStatusState()),
						Translate(T.FORM_OPTION_STATUS_TITLE)
					)
				: null,
			canSetMapColorProperty
				? this.viewOption(
						T.FORM_OPTION_MAP_COLOR,
						CheckBox.view(this.getMapColorState()),
						Translate(T.FORM_OPTION_MAP_COLOR_TITLE)
					)
				: null,
			canSetRequiredProperty
				? this.viewOption(
						T.FORM_OPTION_REQUIRED,
						CheckBox.view(this.getRequiredState()),
						Translate(T.FORM_OPTION_REQUIRED_TITLE)
					)
				: null,
			canSetAddressProperty
				? this.viewOption(
						T.FORM_OPTION_ADDRESS,
						CheckBox.view(this.getAddressState()),
						Translate(T.FORM_OPTION_ADDRESS_TITLE)
					)
				: null,
		]);
	}

	viewOption(name: T | string, value: string | MithrilVNode, title?: string): MithrilVNode {
		return m('div', { className: CSS.input.container, title }, [
			m('div', { className: CSS.input.title }, Translate(name)),
			m('div', { className: CSS.input.value }, value),
		]);
	}

	private async confirmDeleteField() {
		const result = await ModalService.open({
			title: Translate(T.FORM_FIELD_DELETE_TITLE),
			content: Translate(T.FORM_FIELD_DELETE_CONFIRM, {
				name: this.field.displayName || FormDesignerPalette.translate(this.field.type),
			}),
			hasCancel: true,
			okName: T.FORM_FIELD_DELETE,
		});

		if (result.ok) {
			// add stateValue.displayName to empty fields
			// to handle the state issue
			if (this.field.stateValue.displayName === '') {
				this.field.stateValue.displayName = 'empty';
			}

			// if this is either a status, mapColor or address field, delete it from the metadata as well
			const metaStatus = this.field.getForm().metadata.getStatusField();
			if (metaStatus && metaStatus.id === this.field.id) {
				this.field.getForm().metadata.removeStatusField(this.field);
			}

			const metaMapColor = this.field.getForm().metadata.getMapColorField();
			if (metaMapColor && metaMapColor.id === this.field.id) {
				this.field.getForm().metadata.removeMapColorField(this.field);
			}

			const metaAddressLookup = this.field.getForm().metadata.getAddressLookupField();
			if (metaAddressLookup && metaAddressLookup.id === this.field.id) {
				this.field.getForm().metadata.removeAddressLookupField(this.field);
			}

			this.designer.setSelected(this.field.parent);
			this.state.destroy();
			this.field.Mutator.delete();
		}
	}

	private viewFieldMove(): MithrilVNode {
		if (!this.designer.isEditing || this.field.parent == null) {
			return null;
		}
		const index = this.field.parent.getFieldIndex(this.field.id);
		const fieldCount = this.field.parent.fields.length;
		const isFirst = index === 0;
		const isLast = index === fieldCount - 1;
		return m('div', { className: CSS.move }, [
			ImageButton.view(MaterialIcon.KEY_UP, {
				onclick: () => {
					if (isFirst) {
						return;
					}
					this.field.Mutator.moveUp();
					this.designer.setScrollToField(this.field);
					Services.Analytics.trackEvent(Services.Analytics.EventCategory.Mutate, 'form:field:move-up', this.field.id);
				},
				title: T.FORM_FIELD_MOVE_UP,
				disabled: isFirst,
				className: ImageButton.CSS.isSmall,
			}),
			ImageButton.view(MaterialIcon.KEY_DOWN, {
				onclick: () => {
					if (isLast) {
						return;
					}
					this.field.Mutator.moveDown();
					this.designer.setScrollToField(this.field);
					Services.Analytics.trackEvent(Services.Analytics.EventCategory.Mutate, 'form:field:move-down', this.field.id);
				},
				title: T.FORM_FIELD_MOVE_DOWN,
				disabled: isLast,
				className: ImageButton.CSS.isSmall,
			}),
		]);
	}

	private viewMenuItems(): MenuItem[] {
		const subformDepth = this.depth - 1;
		const items: MenuItem[] = [];
		for (const key of Object.keys(FIELD_TYPE_DISPLAY)) {
			const option = FIELD_TYPE_DISPLAY[key];
			const maxDepthReached = option.maxDepth != null && option.maxDepth < subformDepth;
			const hasPermission = Services.User.hasFormPermission(key as IkeFieldType);

			if (hasPermission) {
				items.push({
					view: MenuItemComponent.view(Translate(option.title), option.icon),
					disabled: key === this.field.type || maxDepthReached || !hasPermission,
					action: () => this.changeFieldType(key as IkeFieldType),
				});
			}
		}

		return items;
	}

	getTitleState(): InputState<string> {
		let displayState = this.state.get('display');
		if (displayState == null) {
			displayState = this.state.child(T.FORM_FIELD_DISPLAY_NAME, 'display');
			displayState.isLabelShown = true;
			displayState.isRequired = true;
			displayState.onValue((val) => this.field.Mutator.displayName(val));
			displayState.setValue(this.field.displayName);
			displayState.setAutoFocus(true);
		}

		const form = this.field.getForm();
		const isTypedSubFormFieldOrSubField = Object.values(SubFormType).some((type) => {
			const typedSubForm = form.getTypedSubForm(type);
			if (typedSubForm == null) return false;
			const subFieldIds = typedSubForm.typedStateFieldIds;
			if (subFieldIds === null) return false;
			// if this field is typed subform field, or is a span subform subfield, we can still edit the field name
			return typedSubForm.id === this.field.id || subFieldIds.includes(this.field.id);
		});

		displayState.readOnly = (this.field.isLocked && !isTypedSubFormFieldOrSubField) || !this.designer.isEditing;

		return displayState;
	}

	private getLabelableState(): CheckBoxState {
		return {
			isSelected: () => this.field.title,
			setSelected: (val) => {
				this.setTitle(val);
				Services.Analytics.trackEvent(Services.Analytics.EventCategory.Mutate, 'form:field:label', String(val));
			},
			isDisabled: !this.designer.isEditing,
		};
	}

	private getSecondarylabelableState(): CheckBoxState {
		return {
			isSelected: () => this.field.subtitle,
			setSelected: (val) => {
				this.setSubtitle(val);
				Services.Analytics.trackEvent(
					Services.Analytics.EventCategory.Mutate,
					'form:field:secondarylabel',
					String(val)
				);
			},
			isDisabled: !this.designer.isEditing,
		};
	}

	private getHiddenState(): CheckBoxState {
		return {
			isSelected: () => this.field.collector === IkeCollectorType.OFFICE || this.isParentHidden(),
			setSelected: (val) => {
				this.setHidden(val);
				Services.Analytics.trackEvent(Services.Analytics.EventCategory.Mutate, 'form:field:hidden', String(val));
			},
			isDisabled: !this.designer.isEditing || this.isParentHidden(),
		};
	}

	private getAdminOnlyState(): CheckBoxState {
		return {
			isSelected: () => this.field.adminOnly === true || this.isParentAdminOnly(),
			setSelected: (val) => {
				this.setAdminOnly(val);
				Services.Analytics.trackEvent(Services.Analytics.EventCategory.Mutate, 'form:field:adminOnly', String(val));
			},
			isDisabled: !this.designer.isEditing || this.isParentAdminOnly(),
		};
	}

	private getCloneableState(): CheckBoxState {
		return {
			isSelected: () => this.field.cloneable != null && this.field.cloneable !== IkeCloneable.NONE,
			setSelected: (val) => {
				this.setCloneable(val);
				Services.Analytics.trackEvent(Services.Analytics.EventCategory.Mutate, 'form:field:cloneable', String(val));
			},
			isDisabled: !this.designer.isEditing || !this.isParentCloneable(),
		};
	}

	private getStatusState(): CheckBoxState {
		return {
			isSelected: () => this.field.isStatusField,
			setSelected: (val) => {
				this.setStatusField(val);
				Services.Analytics.trackEvent(Services.Analytics.EventCategory.Mutate, 'form:field:status', String(val));
			},
			isDisabled: !this.designer.isEditing || this.isParentHidden(),
		};
	}

	private getMapColorState(): CheckBoxState {
		return {
			isSelected: () => this.field.isMapColorField,
			setSelected: (val) => {
				this.setMapColorField(val);
				Services.Analytics.trackEvent(Services.Analytics.EventCategory.Mutate, 'form:field:mapcolor', String(val));
			},
			isDisabled: !this.designer.isEditing || this.isParentHidden(),
		};
	}

	private getRequiredState(): CheckBoxState {
		return {
			isSelected: () => this.field.required === true,
			setSelected: (val) => {
				this.setRequired(val);
				Services.Analytics.trackEvent(Services.Analytics.EventCategory.Mutate, 'form:field:required', String(val));
			},
			isDisabled: !this.designer.isEditing || this.isRequiredDisabled(),
		};
	}

	private getAddressState(): CheckBoxState {
		return {
			isSelected: () => this.field.isAddressLookupField,
			setSelected: (val) => {
				this.setAddressLookupField(val);
				Services.Analytics.trackEvent(Services.Analytics.EventCategory.Mutate, 'form:field:address', String(val));
			},
			isDisabled: !this.designer.isEditing || this.isParentHidden(),
		};
	}

	private isParentCloneable(): boolean {
		if (this.field.parent.parent == null) {
			return true;
		}

		return this.field.parent.cloneable != null && this.field.parent.cloneable !== IkeCloneable.NONE;
	}

	private isParentAdminOnly(): boolean {
		if (this.field.parent.parent == null) {
			return false;
		}

		return this.field.parent.adminOnly;
	}

	private isParentHidden(): boolean {
		if (this.field.parent.parent == null) {
			return false;
		}

		return this.field.parent.collector === IkeCollectorType.OFFICE;
	}

	private isRequiredDisabled(): boolean {
		const adminField = this.field.adminOnly;
		const hiddenField = this.field.collector === IkeCollectorType.OFFICE;

		return adminField || hiddenField;
	}

	@action private setTitle(title: boolean) {
		this.field.Mutator.title(title);
	}

	@action private setSubtitle(subtitle: boolean) {
		this.field.Mutator.subtitle(subtitle);
	}

	@action private setHidden(hidden: boolean) {
		if (hidden) {
			this.field.Mutator.collector(IkeCollectorType.OFFICE);
			this.field.Mutator.required(false);
		} else {
			this.field.Mutator.collector(IkeCollectorType.ALL);
		}
	}

	@action private setAdminOnly(adminOnly: boolean) {
		this.field.Mutator.adminOnly(adminOnly);
		if (adminOnly) {
			this.field.Mutator.required(false);
		}
	}

	@action private setCloneable(cloneable: boolean) {
		if (cloneable) {
			this.field.Mutator.cloneable(IkeCloneable.FIRST);
		} else {
			this.field.Mutator.cloneable(IkeCloneable.NONE);
		}
	}

	@action protected setAnnotationLabelFieldId(fieldId: string) {
		this.field.Mutator.annotationLabelFieldId(fieldId);
	}

	@action protected setClearanceThresholdInMeters(clearanceThresholdInMeters: number | null) {
		this.field.Mutator.clearanceThresholdInMeters(clearanceThresholdInMeters);
	}

	@action private setStatusField(useAsStatus: boolean) {
		this.field.Mutator.isStatusField(useAsStatus);
	}

	@action private setMapColorField(useAsMapColor: boolean) {
		this.field.Mutator.isMapColorField(useAsMapColor);
	}

	@action private setRequired(required: boolean) {
		this.field.Mutator.required(required);
	}

	@action private setAddressLookupField(address: boolean) {
		this.field.Mutator.isAddressLookupField(address);
	}
}
